import { z } from 'zod';
import { RUSH_MULTIPLIERS } from './constants.js';
import { BelowFloorError, BelowMinimumError, ValidationError } from './errors.js';
import { computeBlankCost, getCPC, getExtraColorUpcharge, getLocationUpcharge, getSPFloorRate, getSPRetailPrintRate, getSPSetup, getSPWholesalePrintRate, round2, } from './helpers.js';
import { DEFAULT_PRICING_CONFIG_BODY } from './pricing-config/defaults.js';
const SizesSchema = z
    .object({
    sxl: z.number().int().min(0),
    xxl: z.number().int().min(0),
    xxxl: z.number().int().min(0),
    xxxxl: z.number().int().min(0).default(0),
    xxxxxl: z.number().int().min(0).default(0),
})
    .refine((s) => s.sxl + s.xxl + s.xxxl + s.xxxxl + s.xxxxxl > 0, {
    message: 'Total quantity across sizes must be greater than zero.',
});
const LocationSlotSchema = z.object({
    colors: z.number().int().min(1).max(8),
});
const LocationsSchema = z
    .object({
    front: LocationSlotSchema.optional(),
    back: LocationSlotSchema.optional(),
    sleeve: LocationSlotSchema.optional(),
})
    .refine((l) => l.front !== undefined || l.back !== undefined || l.sleeve !== undefined, {
    message: 'At least one of `front`, `back`, or `sleeve` must be provided.',
});
const PerSizeBlankCostSchema = z
    .object({
    sxl: z.number().min(0).optional(),
    xxl: z.number().min(0).optional(),
    xxxl: z.number().min(0).optional(),
    xxxxl: z.number().min(0).optional(),
    xxxxxl: z.number().min(0).optional(),
})
    .optional();
const GarmentSchema = z.discriminatedUnion('source', [
    z.object({
        source: z.literal('we_provide'),
        blankCostEach: z.number().min(0),
        /**
         * Optional per-size blank cost override. When set, the engine uses
         * these prices per bucket instead of `blankCostEach + SIZE_UPCHARGE`.
         * Populated by the rest-api's SS Activewear resolution from real
         * per-band `customerPrice` values. Each key is independent; missing
         * keys fall back to `blankCostEach + SIZE_UPCHARGE[key]`.
         */
        blankCostPerSize: PerSizeBlankCostSchema,
        garmentColor: z.enum(['white', 'colored']),
    }),
    z.object({
        source: z.literal('customer_provides'),
        garmentColor: z.enum(['white', 'colored']),
    }),
]);
// RUSH_MULTIPLIERS lives in constants.ts (re-exported from
// `pricing-config/defaults.ts`). Re-export here so the previously-public
// `RushLevel` type keeps working for callers.
export { RUSH_MULTIPLIERS };
export const QuoteScreenPrintInputSchema = z.object({
    sizes: SizesSchema,
    locations: LocationsSchema,
    garment: GarmentSchema,
    mode: z.enum(['b2c', 'b2b']),
    discountPct: z.number().min(0).max(100),
    rush: z.enum(['none', 'rush', 'hot_rush']).default('none'),
});
export const QuoteScreenPrintBreakdownSchema = z
    .object({
    blankCostPerUnit: z.number(),
    sizeUpchargeTotal: z.number(),
    printRate: z.number(),
    extraColorCharge: z.number(),
    extraLocationCharge: z.number(),
    setupAmortizedPerUnit: z.number(),
    discountPerUnit: z.number(),
    darkUnderbasePerUnit: z.number(),
})
    .meta({
    id: 'QuoteScreenPrintBreakdown',
    description: 'Per-unit cost/pricing components for a screen print quote.',
});
export const QuoteScreenPrintResultSchema = z
    .object({
    mode: z.enum(['b2c', 'b2b']),
    totalQty: z.number().int(),
    perUnitSell: z.number(),
    perUnitCost: z.number(),
    orderTotal: z.number(),
    profit: z.number(),
    marginPct: z.number(),
    floorPerUnit: z.number(),
    setupFee: z.number(),
    breakdown: QuoteScreenPrintBreakdownSchema,
})
    .meta({
    id: 'QuoteScreenPrintResult',
    description: 'Screen print quote output with per-unit sell, totals, and breakdown.',
});
/**
 * B2C + B2B screen print pricing.
 *
 * Floor enforcement: when computed per-unit sell drops below the tier floor,
 * this throws `BelowFloorError` in BOTH modes — callers can't ship below-cost
 * quotes at the engine boundary.
 *
 * Dark-underbase accounting is asymmetric by mode (intentional):
 *   - B2C: `colored` retail tier already bakes in the dark-garment premium.
 *     `darkUnderbase` is excluded from sell (not double-charged) but included
 *     in the cost side of margin so profit reflects real production cost.
 *   - B2B: wholesale rate is color-agnostic. `darkUnderbase` is added to
 *     sell as an explicit surcharge and excluded from the cost side of margin
 *     (treated as 100% margin on paper). Reported margin on B2B colored jobs
 *     therefore reads higher than the "real" margin would if underbase cost
 *     were accounted.
 *
 * Unifying this asymmetry is a business decision, not a code decision: it
 * would require recalibrating B2B wholesale tiers with a color axis, then
 * dropping SP_DARK_UNDERBASE. Left intentional until then.
 */
export function quoteScreenPrint(rawInput, config) {
    const parsed = QuoteScreenPrintInputSchema.safeParse(rawInput);
    if (!parsed.success) {
        throw new ValidationError(parsed.error.issues);
    }
    const input = parsed.data;
    const cfg = config ?? DEFAULT_PRICING_CONFIG_BODY;
    const sp = cfg.screenPrint;
    const sizeUpcharge = cfg.universal.sizeUpcharge;
    const blankCostEach = input.garment.source === 'we_provide' ? input.garment.blankCostEach : 0;
    const blankCostPerSize = input.garment.source === 'we_provide' ? input.garment.blankCostPerSize : undefined;
    const blankCostResult = computeBlankCost(input.sizes, blankCostEach, blankCostPerSize, sizeUpcharge);
    const totalQty = blankCostResult.totalQty;
    if (totalQty < sp.minQty) {
        throw new BelowMinimumError('screen_print', totalQty, sp.minQty);
    }
    // Preserve existing `sizeAgg` shape for downstream references.
    const sizeAgg = {
        totalQty,
        totalUpcharge: blankCostResult.sizeUpchargeTotal,
        perSize: blankCostResult.perSize,
    };
    const avgBlank = totalQty > 0 ? blankCostResult.totalBlankCost / totalQty : 0;
    const { garmentColor } = input.garment;
    // Dark-underbase: colored-only surcharge.
    const darkUnderbase = garmentColor === 'colored'
        ? totalQty >= sp.darkUnderbase.qtyBreak
            ? sp.darkUnderbase.atOrAbove
            : sp.darkUnderbase.below
        : 0;
    const locSlots = [input.locations.front, input.locations.back, input.locations.sleeve].filter((x) => x !== undefined);
    const numLocs = locSlots.length;
    const totalExtraColors = locSlots.reduce((acc, l) => acc + Math.max(0, l.colors - 1), 0);
    const extraLocs = Math.max(0, numLocs - 1);
    const extraColorRate = getExtraColorUpcharge(totalQty, sp.extraColorUpcharge);
    const locUpchargeRate = getLocationUpcharge(totalQty, sp.locationUpcharge);
    const extraColorCharge = totalExtraColors * extraColorRate;
    const extraLocationCharge = extraLocs * locUpchargeRate;
    // Internal production cost (used for margin calc).
    const cpc = getCPC(totalQty, sp.cpcTiers);
    const baseColorCost = cpc;
    const extraColorCost = totalExtraColors * cpc;
    const extraLocInternal = extraLocs * sp.internalLocationCost;
    const printCostInternal = baseColorCost + extraColorCost + extraLocInternal;
    // Floor (B2B-style, used for enforcement in both modes).
    const floorRate = getSPFloorRate(totalQty, sp.floorTiers);
    const floorPerUnit = avgBlank + floorRate + extraColorCharge + extraLocationCharge + darkUnderbase;
    const discountMultiplier = 1 - input.discountPct / 100;
    const rushMultiplier = sp.rushMultipliers[input.rush];
    if (input.mode === 'b2c') {
        const retailRate = getSPRetailPrintRate(totalQty, garmentColor, sp.retailPrintTiers);
        const retailSetup = getSPSetup(totalQty, sp.setupTiers);
        const retailSetupPerUnit = retailSetup / totalQty;
        const preDiscount = retailRate + extraColorCharge + extraLocationCharge + retailSetupPerUnit;
        const printRevenue = preDiscount * discountMultiplier;
        const discountPerUnit = preDiscount * (input.discountPct / 100);
        const preRushSell = avgBlank + printRevenue;
        // Floor check runs against pre-rush sell. Rush is sell-side markup
        // and must never paper over a below-cost quote.
        if (preRushSell < floorPerUnit) {
            throw new BelowFloorError('screen_print', round2(preRushSell), round2(floorPerUnit), `b2c qty ${totalQty}`);
        }
        const sell = preRushSell * rushMultiplier;
        // B2C cost includes darkUnderbase (real production cost).
        const perUnitCost = avgBlank + printCostInternal + retailSetupPerUnit + darkUnderbase;
        const profit = (sell - perUnitCost) * totalQty;
        const marginPct = sell > 0 ? ((sell - perUnitCost) / sell) * 100 : 0;
        return {
            mode: 'b2c',
            totalQty,
            perUnitSell: round2(sell),
            perUnitCost: round2(perUnitCost),
            orderTotal: round2(sell * totalQty),
            profit: round2(profit),
            marginPct: round2(marginPct),
            floorPerUnit: round2(floorPerUnit),
            setupFee: retailSetup,
            breakdown: {
                blankCostPerUnit: round2(avgBlank),
                sizeUpchargeTotal: sizeAgg.totalUpcharge,
                printRate: retailRate,
                extraColorCharge: round2(extraColorCharge),
                extraLocationCharge: round2(extraLocationCharge),
                setupAmortizedPerUnit: round2(retailSetupPerUnit),
                discountPerUnit: round2(discountPerUnit),
                darkUnderbasePerUnit: darkUnderbase,
            },
        };
    }
    // B2B
    const wholesaleRate = getSPWholesalePrintRate(totalQty, sp.wholesalePrintTiers);
    const preDiscount = wholesaleRate + extraColorCharge + extraLocationCharge;
    const printRevenue = preDiscount * discountMultiplier;
    const discountPerUnit = preDiscount * (input.discountPct / 100);
    const preRushSell = avgBlank + printRevenue + darkUnderbase;
    if (preRushSell < floorPerUnit) {
        throw new BelowFloorError('screen_print', round2(preRushSell), round2(floorPerUnit), `b2b qty ${totalQty}`);
    }
    const sell = preRushSell * rushMultiplier;
    // B2B cost-basis excludes darkUnderbase — see header docblock for the asymmetry.
    const perUnitCost = avgBlank + printCostInternal;
    const profit = (sell - perUnitCost) * totalQty;
    const marginPct = sell > 0 ? ((sell - perUnitCost) / sell) * 100 : 0;
    return {
        mode: 'b2b',
        totalQty,
        perUnitSell: round2(sell),
        perUnitCost: round2(perUnitCost),
        orderTotal: round2(sell * totalQty),
        profit: round2(profit),
        marginPct: round2(marginPct),
        floorPerUnit: round2(floorPerUnit),
        setupFee: 0,
        breakdown: {
            blankCostPerUnit: round2(avgBlank),
            sizeUpchargeTotal: sizeAgg.totalUpcharge,
            printRate: wholesaleRate,
            extraColorCharge: round2(extraColorCharge),
            extraLocationCharge: round2(extraLocationCharge),
            setupAmortizedPerUnit: 0,
            discountPerUnit: round2(discountPerUnit),
            darkUnderbasePerUnit: darkUnderbase,
        },
    };
}
//# sourceMappingURL=quote-screen-print.js.map